import { marketAPI } from 'api/marketAPI';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import produce from 'immer';

export const fetchTopRanks = createAsyncThunk(
    'market/fetchTopRanks',
    async (params) => await marketAPI.fetchTopRanks(params)
);

export const fetchPeerComparison = createAsyncThunk(
    'market/fetchPeerComparison',
    async (params) => await marketAPI.fetchPeerComparison(params)
);

export const fetchTrendAnalysis = createAsyncThunk(
    'market/fetchTrendAnalysis',
    async (params) => await marketAPI.fetchTrendAnalysis(params)
);

export const fetchTopAndEmergingSpikes = createAsyncThunk(
    'market/fetchTopAndEmergingSpikes',
    async (params) => await marketAPI.fetchTopAndEmergingSpikes(params)
);

export const fetchScenarioStats = createAsyncThunk(
    'market/fetchScenarioStats',
    async (params) => await marketAPI.fetchScenarioStats(params)
);

const marketSlice = createSlice({
    name: 'market',
    initialState: {
        topFiveRmsData: null,
        topFiveTeamsData: null,
        peerComparisonChartData: null,
        trendAnalysisChartData: null,

        minorThreshold: null,
        majorThreshold: null,
        topFiveEmergingMajorRm: null,
        topFiveEmergingModerateRm: null,
        topFiveRmsSpike: null,

        statsReporting: null,

        topRanksFetched: false,
        peerComparisonChartDataFetched: false,
        trendAnalysisChartDataFetched: false,
        topAndEmergingSpikesFetched: false,
        statsReportingFetched: false
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTopRanks.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { topFiveRm, topFiveTeams } = payload;
                draft.topFiveRmsData = topFiveRm;
                draft.topFiveTeamsData = topFiveTeams;
                draft.topRanksFetched = true;

                if (topFiveRm.length > 0) {
                    const { moderateThreshold, majorThreshold } = topFiveRm[0];
                    draft.minorThreshold = moderateThreshold;
                    draft.majorThreshold = majorThreshold;
                }
            })
        );
        builder.addCase(fetchPeerComparison.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { peerComparisonChartData } = payload;
                draft.peerComparisonChartData = peerComparisonChartData;
                draft.peerComparisonChartDataFetched = true;
            })
        );
        builder.addCase(fetchTrendAnalysis.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { trendAnalysisChartData } = payload;
                draft.trendAnalysisChartData = trendAnalysisChartData;
                draft.trendAnalysisChartDataFetched = true;
            })
        );
        builder.addCase(
            fetchTopAndEmergingSpikes.fulfilled,
            (state, { payload }) =>
                produce(state, (draft) => {
                    const {
                        topFiveEmergingMajorRm,
                        topFiveEmergingModerateRm,
                        topFiveRmSpike
                    } = payload;
                    const emergingRmsToRetrieveThreshold = () => {
                        if (topFiveRmSpike.length > 0) return topFiveRmSpike;
                        if (topFiveEmergingModerateRm.length > 0)
                            return topFiveEmergingModerateRm;
                        if (topFiveEmergingMajorRm.length > 0)
                            return topFiveEmergingMajorRm;
                        return [];
                    };
                    if (emergingRmsToRetrieveThreshold().length > 0) {
                        const { moderateThreshold, majorThreshold } =
                            emergingRmsToRetrieveThreshold()[0];
                        draft.minorThreshold = moderateThreshold;
                        draft.majorThreshold = majorThreshold;
                    }
                    draft.topFiveEmergingMajorRm = topFiveEmergingMajorRm;
                    draft.topFiveEmergingModerateRm = topFiveEmergingModerateRm;
                    draft.topFiveRmsSpike = topFiveRmSpike;
                    draft.topAndEmergingSpikesFetched = true;
                })
        );
        builder.addCase(fetchScenarioStats.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { statsReporting } = payload;
                draft.statsReporting = statsReporting;
                draft.statsReportingFetched = true;
            })
        );
    }
});

const { reducer: marketReducer } = marketSlice;
export default marketReducer;
